import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Group, Select, Stack, Table, Text } from '@grovus/design-system'
import { fetchActivity, type ActivityEntry, type ActivityKind, type Role } from '../data/activity'
import { ACTIVITY_COPY, type ActivityCopy, type Lang } from '../i18n'
import { fmt } from '../lib/format'
import { shareDate } from '../share/date'

interface Props {
  companyId: string
  lang: Lang
  /** Called once when a read answers `404`: the caller is no longer a member. */
  onAccessLost?: (companyId: string) => void
  /** Changing it reloads the first page; the host bumps it after a membership action. */
  refreshKey?: number
  /** `company` reads the company events only, with no filter: «История компании» on Settings. */
  scope?: 'people' | 'company'
  fetchImpl?: typeof fetch
}

type View =
  | { phase: 'loading' }
  | { phase: 'failed' }
  | { phase: 'hidden' }
  | {
      phase: 'ready'
      entries: ActivityEntry[]
      unsupported: number
      next: string | null
      more: 'idle' | 'loading' | 'failed'
    }

/** The row band behind day headers, as screen 10 draws it in both schemes. */
const DAY_BG = 'light-dark(var(--mantine-color-ink-0), var(--mantine-color-dark-6))'
const BORDER = '1px solid var(--mantine-color-default-border)'

interface Row {
  who: string
  what: string
  whom: string | null
}

function role(copy: ActivityCopy, value: Role | null): string {
  return value === null ? '' : copy.roles[value]
}

/** Who did it, what happened and to whom, in screen 10's words. */
export function describe(copy: ActivityCopy, entry: ActivityEntry, dateOf: (at: string | undefined) => string = (at) => at ?? ''): Row {
  const by = entry.actor ?? copy.grovus
  switch (entry.event) {
    case 'invited':
      return { who: by, what: fmt(copy.invited, { role: role(copy, entry.toRole) }), whom: entry.target }
    case 'invite_resent':
      return { who: by, what: copy.resent, whom: entry.target }
    case 'invite_revoked':
      return { who: by, what: copy.revoked, whom: entry.target }
    case 'invite_auto_revoked': {
      const inviter = entry.inviter ?? ''
      const cause = entry.cause
      let what = copy.autoOther
      if (entry.actor === null) what = copy.autoRecovery
      else if (cause?.event === 'role_changed' && inviter !== '') {
        what = fmt(copy.autoRoleChanged, { inviter, from: role(copy, cause.fromRole), to: role(copy, cause.toRole) })
      } else if (cause?.event === 'member_removed' && inviter !== '') {
        what = fmt(copy.autoRemoved, { inviter })
      } else if (cause?.event === 'member_left' && inviter !== '') {
        what = fmt(copy.autoLeft, { inviter })
      }
      return { who: copy.grovus, what, whom: entry.target }
    }
    case 'invite_accepted':
      return { who: entry.target ?? '', what: fmt(copy.accepted, { role: role(copy, entry.toRole) }), whom: null }
    case 'invite_declined':
      return { who: entry.target ?? '', what: copy.declined, whom: null }
    case 'role_changed':
      return {
        who: by,
        what: fmt(copy.roleChanged, { from: role(copy, entry.fromRole), to: role(copy, entry.toRole) }),
        whom: entry.target,
      }
    case 'member_removed':
      return { who: by, what: copy.removed, whom: entry.target }
    case 'member_left':
      return { who: entry.target ?? '', what: copy.left, whom: null }
    case 'share_link_created':
      return { who: by, what: fmt(copy.linkCreated, { label: linkLabel(entry), date: dateOf(entry.shareLink?.expiresAt) }), whom: null }
    case 'share_link_revoked':
      return { who: by, what: fmt(copy.linkRevoked, { label: linkLabel(entry) }), whom: null }
    case 'share_link_auto_revoked': {
      const label = linkLabel(entry)
      const cause = entry.cause
      let what = fmt(copy.linkAutoOther, { label })
      if (entry.actor === null) what = fmt(copy.linkAutoOperator, { label })
      else if (cause?.event === 'role_changed') {
        what = fmt(copy.linkAutoRoleChanged, { label, from: role(copy, cause.fromRole), to: role(copy, cause.toRole) })
      } else if (cause?.event === 'member_removed') what = fmt(copy.linkAutoRemoved, { label })
      else if (cause?.event === 'member_left') what = fmt(copy.linkAutoLeft, { label })
      return { who: copy.grovus, what, whom: null }
    }
    case 'company_created':
      return { who: by, what: copy.companyCreated, whom: entry.target }
    case 'company_renamed':
      return { who: by, what: fmt(copy.companyRenamed, { from: entry.renamed?.from ?? '', to: entry.renamed?.to ?? '' }), whom: null }
    case 'deletion_requested':
      return { who: by, what: copy.deletionRequested, whom: null }
    case 'company_restored':
      return { who: by, what: copy.companyRestored, whom: null }
  }
}

function linkLabel(entry: ActivityEntry): string {
  const label = entry.shareLink?.label
  return label === null || label === undefined ? '' : ` (${label})`
}

interface Day {
  key: string
  label: string
  entries: ActivityEntry[]
}

/** Entries grouped by the viewer's local day, in the order they arrived. */
function days(entries: ActivityEntry[], lang: Lang): Day[] {
  const locale: Record<Lang, string> = { en: 'en-GB', ru: 'ru-RU', uk: 'uk-UA' }
  const label = new Intl.DateTimeFormat(locale[lang], { day: 'numeric', month: 'long', year: 'numeric' })
  const groups: Day[] = []
  for (const entry of entries) {
    const at = new Date(entry.at)
    const key = `${at.getFullYear()}-${at.getMonth()}-${at.getDate()}`
    const last = groups[groups.length - 1]
    if (last?.key === key) last.entries.push(entry)
    else groups.push({ key, label: label.format(at), entries: [entry] })
  }
  return groups
}

function timeOf(at: string, lang: Lang): string {
  const locale: Record<Lang, string> = { en: 'en-US', ru: 'ru-RU', uk: 'uk-UA' }
  return new Intl.DateTimeFormat(locale[lang], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: lang === 'en',
  }).format(new Date(at))
}

function Who({ row }: { row: Row }) {
  return <Text component="span" fw={500} style={{ overflowWrap: 'anywhere' }}>{row.who}</Text>
}

function Whom({ row }: { row: Row }) {
  return row.whom === null
    ? <Text component="span" className="grv-muted">-</Text>
    : <Text component="span" fw={500} style={{ overflowWrap: 'anywhere' }}>{row.whom}</Text>
}

/**
 * The activity log section of the Members page (screen 10): Owners and Admins see who
 * was invited, joined, changed role, was removed or left, and by whom. GRO-106 mounts it.
 *
 * Every activation (the first render, and each change of company, filter or
 * `refreshKey`) takes a new generation, and a response is applied only while its
 * generation is current, so a late answer for an earlier scope, or one after unmount,
 * never reaches the screen or the host.
 */
export function ActivityLog({ companyId, lang, onAccessLost, refreshKey = 0, scope = 'people', fetchImpl = fetch }: Props) {
  const copy = ACTIVITY_COPY[lang]
  const company = scope === 'company'
  const [kind, setKind] = useState<ActivityKind>(company ? 'company' : 'all')
  const [reload, setReload] = useState(0)
  const [view, setView] = useState<View>({ phase: 'loading' })
  const generation = useRef(0)
  const moreInFlight = useRef(false)
  const lost = useRef(onAccessLost)
  lost.current = onAccessLost

  useEffect(() => {
    const mine = ++generation.current
    moreInFlight.current = false
    setView({ phase: 'loading' })
    void fetchActivity(companyId, kind, null, fetchImpl).then((result) => {
      if (mine !== generation.current) return
      if (result.status === 'ok') {
        setView({ phase: 'ready', entries: result.entries, unsupported: result.unsupported, next: result.next, more: 'idle' })
      } else if (result.status === 'unavailable') {
        setView({ phase: 'failed' })
      } else {
        setView({ phase: 'hidden' })
        if (result.status === 'not_found') lost.current?.(companyId)
      }
    })
    return () => {
      // Retire this activation, so a response that arrives after a scope change or an
      // unmount, from either request path, is discarded.
      if (generation.current === mine) generation.current += 1
    }
  }, [companyId, kind, refreshKey, reload, fetchImpl])

  const loadMore = useCallback(() => {
    if (view.phase !== 'ready' || view.next === null || moreInFlight.current) return
    const mine = generation.current
    const cursor = view.next
    moreInFlight.current = true
    setView({ ...view, more: 'loading' })
    void fetchActivity(companyId, kind, cursor, fetchImpl).then((result) => {
      if (mine !== generation.current) return
      moreInFlight.current = false
      setView((current) => {
        if (current.phase !== 'ready') return current
        if (result.status === 'ok') {
          return {
            phase: 'ready',
            entries: [...current.entries, ...result.entries],
            unsupported: current.unsupported + result.unsupported,
            next: result.next,
            more: 'idle',
          }
        }
        if (result.status === 'unavailable') return { ...current, more: 'failed' }
        return { phase: 'hidden' }
      })
      if (result.status === 'not_found') lost.current?.(companyId)
    })
  }, [view, companyId, kind, fetchImpl])

  if (view.phase === 'hidden') return null

  const emptyLog = view.phase === 'ready' && view.entries.length === 0 && view.unsupported === 0 && view.next === null
  const showFilter = !company && !(emptyLog && kind === 'all')
  const filterData = [
    { value: 'all', label: copy.filterAll },
    { value: 'invitations', label: copy.filterInvitations },
    { value: 'roles', label: copy.filterRoles },
    { value: 'removals', label: copy.filterRemovals },
    { value: 'sharing', label: copy.filterSharing },
  ]

  return (
    <Stack gap={12} data-testid="activity-log">
      <Group justify="space-between" gap="8px 16px" wrap="wrap">
        <Text component="h2" className="grv-label" m={0}>{company ? copy.companyTitle : copy.title}</Text>
        {showFilter && (
          <Select
            data={filterData}
            value={kind}
            onChange={(value) => value !== null && setKind(value as ActivityKind)}
            allowDeselect={false}
            aria-label={copy.filter}
            w={{ base: '100%', sm: 210 }}
          />
        )}
      </Group>

      {view.phase === 'loading' && <Text className="grv-muted" role="status">{copy.loading}</Text>}

      {view.phase === 'failed' && (
        <Group gap="xs" role="alert">
          <Text className="grv-muted">{copy.loadFailed}</Text>
          <Button size="compact-sm" variant="default" onClick={() => setReload((n) => n + 1)}>{copy.retry}</Button>
        </Group>
      )}

      {emptyLog && (
        <Box p={24} style={{ border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }}>
          <Text className="grv-muted" maw={520} style={{ textWrap: 'pretty' }}>
            {company ? copy.companyEmpty : kind === 'all' ? copy.empty : copy.noMatch}
          </Text>
        </Box>
      )}

      {view.phase === 'ready' && !emptyLog && (
        <>
          {view.entries.length > 0 && <Entries entries={view.entries} copy={copy} lang={lang} />}
          {view.unsupported > 0 && <Text className="grv-muted" fz="sm">{copy.newer}</Text>}
          {view.more === 'failed' && (
            <Group gap="xs" role="alert">
              <Text className="grv-muted">{copy.moreFailed}</Text>
              <Button size="compact-sm" variant="default" onClick={loadMore}>{copy.retry}</Button>
            </Group>
          )}
          {view.next !== null && view.more !== 'failed' && (
            <Group>
              <Button variant="default" size="sm" onClick={loadMore} disabled={view.more === 'loading'}>
                {copy.more}
              </Button>
            </Group>
          )}
        </>
      )}
    </Stack>
  )
}

function Entries({ entries, copy, lang }: { entries: ActivityEntry[]; copy: ActivityCopy; lang: Lang }) {
  const grouped = days(entries, lang)
  const dateOf = (at: string | undefined) => at === undefined ? '' : shareDate(at, lang)
  return (
    <>
      <Box visibleFrom="sm" data-testid="activity-wide" style={{ border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }}>
        <Table>
          <colgroup>
            <col style={{ width: 110 }} />
            <col style={{ width: 400 }} />
            <col />
            <col style={{ width: 290 }} />
          </colgroup>
          <Table.Thead>
            <Table.Tr>
              <Table.Th><span className="grv-label">{copy.time}</span></Table.Th>
              <Table.Th><span className="grv-label">{copy.who}</span></Table.Th>
              <Table.Th><span className="grv-label">{copy.what}</span></Table.Th>
              <Table.Th><span className="grv-label">{copy.whom}</span></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {grouped.map((day) => [
              <Table.Tr key={day.key} bg={DAY_BG}>
                <Table.Td colSpan={4} py={6}><span className="grv-label">{day.label}</span></Table.Td>
              </Table.Tr>,
              ...day.entries.map((entry) => {
                const row = describe(copy, entry, dateOf)
                return (
                  <Table.Tr key={entry.id} data-testid="activity-row">
                    <Table.Td><span className="grv-num grv-muted">{timeOf(entry.at, lang)}</span></Table.Td>
                    <Table.Td><Who row={row} /></Table.Td>
                    <Table.Td><Text component="span" style={{ textWrap: 'pretty' }}>{row.what}</Text></Table.Td>
                    <Table.Td><Whom row={row} /></Table.Td>
                  </Table.Tr>
                )
              }),
            ])}
          </Table.Tbody>
        </Table>
      </Box>
      <Box
        hiddenFrom="sm"
        data-testid="activity-narrow"
        style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }}
      >
        {grouped.map((day, index) => (
          <Box key={day.key}>
            <Box px={16} py={8} style={{ borderTop: index === 0 ? 'none' : BORDER, background: DAY_BG }}>
              <span className="grv-label">{day.label}</span>
            </Box>
            {day.entries.map((entry) => {
              const row = describe(copy, entry, dateOf)
              return (
                <Stack key={entry.id} gap={6} px={16} py={12} style={{ borderTop: BORDER }}>
                  <Group gap="4px 8px" wrap="wrap">
                    <span className="grv-num grv-muted">{timeOf(entry.at, lang)}</span>
                    <Who row={row} />
                  </Group>
                  <Text style={{ textWrap: 'pretty' }}>{row.what}</Text>
                  {row.whom !== null && <Whom row={row} />}
                </Stack>
              )
            })}
          </Box>
        ))}
      </Box>
    </>
  )
}
